import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createRowActions } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { UserRoleRowActions } from "./row-actions";

/** One row of the user's roles table: a role of the catalogue, with the user's standing in it. */
export interface UserRoleRow {
	id: string;
	/** The display name, already capitalised for system roles. */
	name: string;
	isSystem: boolean;
	/** Whether the user carries the role. */
	isActive: boolean;
	/** The member system role: always active, never toggleable. */
	isLocked: boolean;
}

const columnHelper = createColumnHelper<UserRoleRow>();

/** Props Interface */
interface CreateColumnsOptions {
	/** The user whose roles the rows describe. */
	userId: string;
	/** Toggle menus render only for a caller `canManageRoles` admits — see `useCitadel`. The
	 * column itself always exists: the member row's standing-membership hint belongs to every
	 * reader, not only to those who could try the toggle and be refused. */
	withActions: boolean;
}

/**
 * The user's roles table: every role of the catalogue, its status for this user, and — for
 * callers who may write roles — the activate/deactivate row actions. The locked member row
 * explains itself with a tooltip instead of an action.
 */
export function createColumns({ userId, withActions }: CreateColumnsOptions) {
	return [
		columnHelper.accessor("name", {
			id: "name",
			header: localized("roles.role"),
			cell: (cell) => (
				<div
					className="items-center gap-1.5 h-stack"
					data-cy={`user-role-row-${cell.row.original.id}`}
				>
					<p className="truncate">{cell.getValue()}</p>
				</div>
			),
		}),
		columnHelper.accessor("isActive", {
			id: "status",
			header: localized("general.state"),
			cell: (cell) => (
				<Tag
					color={cell.getValue() ? "green" : "gray"}
					variant="plain"
				>
					{localized(cell.getValue() ? "general.active" : "general.inactive")}
				</Tag>
			),
		}),
		createRowActions<UserRoleRow>((props) => (
			<UserRoleRowActions
				{...props}
				userId={userId}
				withActions={withActions}
			/>
		)),
	];
}
